import React, { useContext, useEffect, useRef, useState } from 'react';
import { Stack, Text, Button, Box, Flex, Spinner, useToast } from '@chakra-ui/react';
import { SocialProfile } from 'src/api/v1-api';
import { MarketplaceContext } from 'src/contexts/marketplace';
import { SocialProfileForm, SocialProfilesTable } from 'src/components/molecules';
import useRequest from 'src/utils/hooks/useRequest';

const SocialProfiles = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [socialProfiles, setSocialProfiles] = useState<SocialProfile[]>([]);
  const [socialProfileSelected, setSocialProfileSelected] = useState<Partial<SocialProfile> | null>(
    null,
  );
  const socialProfileFormRef = useRef<HTMLDivElement>(null);
  const {
    marketplace,
    setMarketplace,
    connectedSocialProfiles,
    setConnectedSocialProfiles,
    setConnectedSocialProfilesFetched,
  } = useContext(MarketplaceContext);
  const { api } = useRequest();
  const toast = useToast();

  useEffect(() => {
    if (marketplace?.social_profiles) {
      setSocialProfiles(marketplace.social_profiles);
    }
  }, [marketplace?.social_profiles]);

  useEffect(() => {
    if (marketplace) {
      getConnectedSocialProfiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketplace]);

  useEffect(() => {
    if (socialProfileSelected && socialProfileFormRef.current) {
      socialProfileFormRef.current.scrollIntoView();
    }
  }, [socialProfileSelected, socialProfileFormRef]);

  const getConnectedSocialProfiles = async () => {
    if (!marketplace) return;

    try {
      setIsLoading(true);
      const response = await api.shareautomation.connectedSocialProfiles.list(marketplace.id);
      setConnectedSocialProfiles(response.connected_accounts);
      setConnectedSocialProfilesFetched(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const createSocialProfile = async (socialProfile: Partial<SocialProfile>) => {
    if (!marketplace) return;
    try {
      setIsLoading(true);
      const response = await api.marketplace.socialProfiles.create(marketplace.id, socialProfile);
      const updatedSocialProfiles = [...socialProfiles, response];
      setSocialProfiles(updatedSocialProfiles);
      setMarketplace({
        ...marketplace,
        social_profiles: updatedSocialProfiles,
      });
      setSocialProfileSelected(null);
      toast({
        title: 'Social Profile Created',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error creating Social Profile. Please try again',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateSocialProfile = async (socialProfile: SocialProfile) => {
    if (!marketplace || !socialProfile?.id) return;

    try {
      setIsLoading(true);
      const response = await api.marketplace.socialProfiles.update(
        marketplace.id,
        socialProfile.id,
        socialProfile,
      );
      const updatedSocialProfiles = socialProfiles.map((sp) =>
        sp.id === socialProfile.id ? response : sp,
      );
      setSocialProfiles(updatedSocialProfiles);
      setMarketplace({
        ...marketplace,
        social_profiles: updatedSocialProfiles,
      });
      if (socialProfileSelected?.id === socialProfile.id) {
        setSocialProfileSelected(null);
      }
      toast({
        title: 'Social Profile Updated',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error updating Social Profile. Please try again',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteSocialProfile = async (socialProfile: SocialProfile) => {
    if (!marketplace || !socialProfile?.id) return;

    try {
      setIsLoading(true);
      await api.marketplace.socialProfiles.delete(marketplace.id, socialProfile.id);
      const updatedSocialProfiles = socialProfiles.filter((sp) => sp.id !== socialProfile.id);
      setSocialProfiles(updatedSocialProfiles);
      setMarketplace({
        ...marketplace,
        social_profiles: updatedSocialProfiles,
      });
      if (socialProfileSelected?.id === socialProfile.id) {
        setSocialProfileSelected(null);
      }
      toast({
        title: 'Social Profile Deleted',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error deleting Social Profile. Please try again',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const newSocialProfile = () => {
    setSocialProfileSelected({});
  };

  return (
    <Stack position="relative">
      <Flex direction="column" gap={12}>
        <Flex direction="column">
          <Flex direction="row" alignItems="center" justifyContent="space-between" pb={4}>
            <Text fontWeight="semibold">Social Profiles</Text>
            <Button onClick={newSocialProfile}>Add New</Button>
          </Flex>
          <SocialProfilesTable
            socialProfiles={socialProfiles}
            connectedSocialProfiles={connectedSocialProfiles}
            editSocialProfile={setSocialProfileSelected}
            onUpdateSocialProfile={updateSocialProfile}
            onDeleteSocialProfile={deleteSocialProfile}
          />
        </Flex>
        <div
          ref={socialProfileFormRef}
          style={{ display: socialProfileSelected ? 'block' : 'none' }}
        >
          <SocialProfileForm
            socialProfile={socialProfileSelected}
            onCancel={() => setSocialProfileSelected(null)}
            onUpdateSocialProfile={updateSocialProfile}
            onCreateSocialProfile={createSocialProfile}
          />
        </div>
        {isLoading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            justifyContent="center"
            alignItems="center"
            backgroundColor="rgba(255, 255, 255, 0.7)"
          >
            <Spinner />
          </Box>
        )}
      </Flex>
    </Stack>
  );
};

export default SocialProfiles;
